import PropTypes from 'prop-types';
import React from 'react';

import { Button, Input, Select } from 'common-src/components/base';
import { apiRequest, getRestErrorMessage, HttpMethods } from 'common-src/features/rest';

import useAlert from 'src/hooks/useAlert';
import { formatLeadPhone } from 'src/utils/leadPhone';

import styles from './LeadResourceCallForm.module.scss';

// Must match the backend PatientSex enum exactly: patient creation rejects anything else.
const SEX_OPTIONS = [
  { label: 'Male', value: 'Male' },
  { label: 'Female', value: 'Female' },
];

// Must stay in sync with REQUIRED_LEAD_FIELDS in the backend's create_patient_from_lead;
// email is optional.
const PATIENT_REQUIRED_FIELDS = [
  'firstName',
  'lastName',
  'phone',
  'dateOfBirth',
  'sex',
  'street',
  'city',
  'state',
  'postalCode',
];

// Mirrors bad_name_characters in the backend's src/core/validators.py. Patient names are
// validated on the model, so a digit here fails patient creation.
const INVALID_NAME_CHARACTERS = '+<>!@#$%^&*()[]{}“`?/\\|;:~=_0123456789';
const NAME_FIELDS = ['firstName', 'lastName'];

const hasInvalidNameCharacters = (value) =>
  `${value ?? ''}`.split('').some((char) => INVALID_NAME_CHARACTERS.includes(char));

const PATIENT_FIELD_LABELS = {
  firstName: 'First Name',
  lastName: 'Last Name',
  phone: 'Phone',
  email: 'Email',
  dateOfBirth: 'Date of Birth',
  sex: 'Sex',
  street: 'Street',
  city: 'City',
  state: 'State',
  postalCode: 'Postal Code',
};

const YES_NO_OPTIONS = [
  { label: 'Yes', value: true },
  { label: 'No', value: false },
];
const YES_NO_TEXT_OPTIONS = [
  { label: 'Yes', value: 'Yes' },
  { label: 'No', value: 'No' },
];

const DIABETES_TYPE_OPTIONS = ['Type 1', 'Type 2', 'Pre-diabetes'].map((value) => ({
  label: value,
  value,
}));

const BUSINESS_UNIT = { diabetes: 'Diabetes', cognitive: 'Cognitive' };

const STATE_OPTIONS = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DC',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
].map((state) => ({ label: state, value: state }));

const EMPTY_FORM = {
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  dateOfBirth: '',
  sex: null,
  street: '',
  streetAddressLine2: '',
  city: '',
  state: '',
  postalCode: '',
  pcpName: '',
  appointmentBooked: null,
  disqualificationReason: '',
  disqualifiedDate: '',
  disqualifiedTime: '',
  caretakerFirstName: '',
  caretakerLastName: '',
  caretakerPhoneNumber: '',
  caretakerRelationship: '',
  mbiNumber: '',
  recentA1cLevel: '',
  recentA1cDate: '',
  diabetesType: null,
  diagnosisDate: '',
  readingFrequency: '',
  takesInsulin: null,
  hasPcp: null,
  onAnotherRemoteMonitoringProgram: null,
  onHospiceOrPalliativeCare: null,
  patientGoal: '',
  hasSmartphone: null,
  hasCgm: null,
  cgmModel: '',
  hasCgmApp: null,
  willingToTakeDailyReadings: null,
  willingToHaveMonthlyVisits: null,
  athenaDepartment: '',
  willAttendOnlineCognitiveScreening: null,
  willAttendNeurologistVisit: null,
  verifiedContractId: '',
  verifiedPlanId: '',
  primaryInsurancePayorId: '',
  primaryInsuranceStart: '',
  primaryInsuranceEnd: '',
  voiceOptIn: null,
  smsOptIn: null,
  emailOptIn: null,
};

const toValue = (value) => (value == null ? '' : `${value}`);

const toNullableValue = (value) => {
  const normalized = `${value ?? ''}`.trim();
  return normalized || null;
};

const toZipCode = (value) => `${value ?? ''}`.replace(/\D/g, '').slice(0, 5);

const toForm = (lead = {}) => ({
  firstName: toValue(lead.firstName),
  lastName: toValue(lead.lastName),
  phone: lead.phone ? formatLeadPhone(lead.phone) : '',
  email: toValue(lead.email),
  dateOfBirth: toValue(lead.dateOfBirth),
  sex: lead.sex || null,
  street: toValue(lead.street),
  streetAddressLine2: toValue(lead.streetAddressLine2),
  city: toValue(lead.city),
  state: toValue(lead.state),
  postalCode: toValue(lead.postalCode),
  pcpName: toValue(lead.pcpName),
  appointmentBooked: lead.appointmentBooked ?? null,
  disqualificationReason: toValue(lead.disqualificationReason),
  disqualifiedDate: toValue(lead.disqualifiedDate),
  disqualifiedTime: toValue(lead.disqualifiedTime),
  caretakerFirstName: toValue(lead.caretakerFirstName),
  caretakerLastName: toValue(lead.caretakerLastName),
  caretakerPhoneNumber: lead.caretakerPhoneNumber ? formatLeadPhone(lead.caretakerPhoneNumber) : '',
  caretakerRelationship: toValue(lead.caretakerRelationship),
  mbiNumber: toValue(lead.mbiNumber),
  recentA1cLevel: toValue(lead.recentA1cLevel),
  recentA1cDate: toValue(lead.recentA1cDate),
  diabetesType: lead.diabetesType || null,
  diagnosisDate: toValue(lead.diagnosisDate),
  readingFrequency: toValue(lead.readingFrequency),
  takesInsulin: lead.takesInsulin || null,
  hasPcp: lead.hasPcp || null,
  onAnotherRemoteMonitoringProgram: lead.onAnotherRemoteMonitoringProgram || null,
  onHospiceOrPalliativeCare: lead.onHospiceOrPalliativeCare || null,
  patientGoal: toValue(lead.patientGoal),
  hasSmartphone: lead.hasSmartphone || null,
  hasCgm: lead.hasCgm || null,
  cgmModel: toValue(lead.cgmModel),
  hasCgmApp: lead.hasCgmApp || null,
  willingToTakeDailyReadings: lead.willingToTakeDailyReadings || null,
  willingToHaveMonthlyVisits: lead.willingToHaveMonthlyVisits || null,
  athenaDepartment: toValue(lead.athenaDepartment),
  willAttendOnlineCognitiveScreening: lead.willAttendOnlineCognitiveScreening || null,
  willAttendNeurologistVisit: lead.willAttendNeurologistVisit || null,
  verifiedContractId: toValue(lead.verifiedContractId),
  verifiedPlanId: toValue(lead.verifiedPlanId),
  primaryInsurancePayorId: toValue(lead.primaryInsurancePayorId),
  primaryInsuranceStart: toValue(lead.primaryInsuranceStart),
  primaryInsuranceEnd: toValue(lead.primaryInsuranceEnd),
  voiceOptIn: lead.voiceOptIn ?? null,
  smsOptIn: lead.smsOptIn ?? null,
  emailOptIn: lead.emailOptIn ?? null,
});

const getPayload = (form) => ({
  FirstName: toNullableValue(form.firstName),
  LastName: toNullableValue(form.lastName),
  Phone: toNullableValue(form.phone),
  Email: toNullableValue(form.email),
  Date_of_Birth__c: toNullableValue(form.dateOfBirth),
  sex: form.sex,
  Street: toNullableValue(form.street),
  Street_Address_Line_2__c: toNullableValue(form.streetAddressLine2),
  City: toNullableValue(form.city),
  State: toNullableValue(form.state),
  PostalCode: toNullableValue(form.postalCode),
  PCP_Name__c: toNullableValue(form.pcpName),
  appointmentBooked: form.appointmentBooked,
  Disqualification_Reason__c: toNullableValue(form.disqualificationReason),
  Disqualified_Date__c: toNullableValue(form.disqualifiedDate),
  Disqualified_Time__c: toNullableValue(form.disqualifiedTime),
  Caretaker_First_Name__c: toNullableValue(form.caretakerFirstName),
  Caretaker_Last_Name__c: toNullableValue(form.caretakerLastName),
  Caretaker_Phone_Number__c: toNullableValue(form.caretakerPhoneNumber),
  Caretaker_Relationship__c: toNullableValue(form.caretakerRelationship),
  MBI_Number__c: toNullableValue(form.mbiNumber),
  Recent_a1c_level__c: toNullableValue(form.recentA1cLevel),
  Recent_a1c_date__c: toNullableValue(form.recentA1cDate),
  Diabetes_Type__c: form.diabetesType,
  Diagnosis_Date__c: toNullableValue(form.diagnosisDate),
  Reading_Frequency__c: toNullableValue(form.readingFrequency),
  Does_patient_take_insulin__c: form.takesInsulin,
  Has_PCP_or_Endo__c: form.hasPcp,
  Is_on_another_remote_monitoring_program__c: form.onAnotherRemoteMonitoringProgram,
  Is_on_hospice_or_palliative_care__c: form.onHospiceOrPalliativeCare,
  Patient_Goal__c: toNullableValue(form.patientGoal),
  Has_Smartphone__c: form.hasSmartphone,
  Has_CGM__c: form.hasCgm,
  CGM_Model__c: toNullableValue(form.cgmModel),
  Has_CGM_App__c: form.hasCgmApp,
  Willing_to_take_daily_readings__c: form.willingToTakeDailyReadings,
  Willing_to_have_monthly_visits__c: form.willingToHaveMonthlyVisits,
  Athena_Department__c: toNullableValue(form.athenaDepartment),
  Will_Attend_Online_Cognitive_Screening__c: form.willAttendOnlineCognitiveScreening,
  Will_Attend_Neurologist_Visit__c: form.willAttendNeurologistVisit,
  Verified_Contract_Id__c: toNullableValue(form.verifiedContractId),
  Verified_Plan_ID__c: toNullableValue(form.verifiedPlanId),
  Primary_Insurance_Payor_ID__c: toNullableValue(form.primaryInsurancePayorId),
  Primary_Insurance_Start__c: toNullableValue(form.primaryInsuranceStart),
  Primary_Insurance_End__c: toNullableValue(form.primaryInsuranceEnd),
  // Communication opt-ins are shown read-only and never written from this form: they are
  // set by the lead source (or the patient) and enrollment must not flip them by hand.
});

const LockedValue = ({ label, value }) => (
  <div className={styles.lockedValue}>
    <span>{label}</span>
    <strong>{toValue(value) || '-'}</strong>
  </div>
);

const NativeField = ({ id, label, type = 'text', value, onChange, errorText, warningText }) => (
  <label className={styles.nativeField} htmlFor={id}>
    <span>{label}</span>
    <input
      id={id}
      type={type}
      className={
        (errorText && styles.nativeInputError) || (warningText && styles.nativeInputWarning) || ''
      }
      value={value}
      onChange={(event) => onChange(event.target.value)}
    />
    {errorText ? <em className={styles.nativeError}>{errorText}</em> : null}
    {!errorText && warningText ? <em className={styles.nativeWarning}>{warningText}</em> : null}
  </label>
);

// Select has no warning state of its own, so the soft (non-blocking) hint is drawn around it.
const SelectField = ({ errorText, warningText, ...props }) => (
  <div className={styles.fieldStack}>
    <Select
      {...props}
      errorText={errorText}
      classNames={!errorText && warningText ? [styles.selectWarning] : []}
    />
    {!errorText && warningText ? <em className={styles.nativeWarning}>{warningText}</em> : null}
  </div>
);

const CheckboxField = ({ id, label, checked, onChange, disabled = false }) => (
  <label
    className={`${styles.checkboxField} ${disabled ? styles.checkboxFieldLocked : ''}`}
    htmlFor={id}
  >
    <input
      id={id}
      type="checkbox"
      checked={checked === true}
      disabled={disabled}
      onChange={(event) => onChange?.(event.target.checked)}
    />
    <span>{label}</span>
  </label>
);

const LeadResourceCallForm = React.forwardRef(
  (
    {
      isPage = false,
      leadResourceId,
      onLeadUpdated,
      onCreatePatient,
      isCreatingPatient = false,
      title = 'Regal call form',
      // A host (the work-lead modal) can render its own Save button and drive the form
      // through the ref; onActionStateChange keeps that button's enabled state in sync.
      hideActions = false,
      onActionStateChange,
    },
    ref,
  ) => {
    const [lead, setLead] = React.useState(null);
    const [loadedLeadResourceId, setLoadedLeadResourceId] = React.useState(null);
    const [form, setForm] = React.useState(EMPTY_FORM);
    const [isLoading, setIsLoading] = React.useState(false);
    const [isSaving, setIsSaving] = React.useState(false);
    const [errorText, setErrorText] = React.useState('');
    const { showAlert, AlertType } = useAlert();

    React.useEffect(() => {
      let isCanceled = false;

      const loadLead = async () => {
        if (!leadResourceId) return;
        setIsLoading(true);
        setLead(null);
        setLoadedLeadResourceId(null);
        setForm(EMPTY_FORM);
        setErrorText('');

        try {
          const response = await apiRequest({ endpoint: `leadResources/${leadResourceId}` });
          const body = await response.json();

          if (!response.ok) {
            throw new Error(getRestErrorMessage(body) || 'Lead resource could not be loaded');
          }

          if (isCanceled) return;
          setLead(body);
          setLoadedLeadResourceId(leadResourceId);
          setForm(toForm(body));
        } catch (error) {
          if (isCanceled) return;
          setErrorText(error?.message || 'Lead resource could not be loaded');
        } finally {
          if (!isCanceled) setIsLoading(false);
        }
      };

      loadLead();

      return () => {
        isCanceled = true;
      };
    }, [leadResourceId]);

    const updateField = (field, value) => {
      setForm((prev) => ({ ...prev, [field]: value }));
    };

    const selectedAppointmentBooked =
      YES_NO_OPTIONS.find((option) => option.value === form.appointmentBooked) || null;
    const selectedState = STATE_OPTIONS.find((option) => option.value === form.state) || null;
    const selectedSex = SEX_OPTIONS.find((option) => option.value === form.sex) || null;
    const selectedYesNo = (value) =>
      YES_NO_TEXT_OPTIONS.find((option) => option.value === value) || null;
    const selectedDiabetesType =
      DIABETES_TYPE_OPTIONS.find((option) => option.value === form.diabetesType) || null;
    const businessUnit = `${lead?.businessUnit || ''}`.trim().toLowerCase();
    const showDiabetesIntake = businessUnit !== BUSINESS_UNIT.cognitive.toLowerCase();
    const showCognitiveIntake = businessUnit !== BUSINESS_UNIT.diabetes.toLowerCase();
    const canSave = Boolean(
      leadResourceId && loadedLeadResourceId === leadResourceId && !isLoading && !errorText,
    );

    const missingPatientFields = PATIENT_REQUIRED_FIELDS.filter((field) => !form[field]);
    const invalidNameFields = NAME_FIELDS.filter(
      (field) => form[field] && hasInvalidNameCharacters(form[field]),
    );
    const canCreatePatient =
      canSave && !isSaving && missingPatientFields.length === 0 && invalidNameFields.length === 0;

    const isCreatePatientStep = Boolean(onCreatePatient);
    const requirementHint = (field) => {
      if (!PATIENT_REQUIRED_FIELDS.includes(field)) return '';
      if (!form[field]) {
        return isCreatePatientStep ? 'Required to create patient' : 'Needed to create patient';
      }
      if (invalidNameFields.includes(field)) return 'No numbers or symbols allowed';
      return '';
    };
    const fieldFeedback = (field) => {
      const hint = requirementHint(field);
      if (!hint) return {};
      return isCreatePatientStep ? { errorText: hint } : { warningText: hint };
    };
    const inputFeedback = (field) => {
      const feedback = fieldFeedback(field);
      return feedback.warningText
        ? { ...feedback, inputClassNames: [styles.inputWarning] }
        : feedback;
    };
    const toLabel = (field) => PATIENT_FIELD_LABELS[field] || field;
    const missingFieldLabels = missingPatientFields.map(toLabel);
    const invalidFieldLabels = invalidNameFields.map(toLabel);

    const handleSave = async () => {
      if (!canSave || isSaving) return;
      setIsSaving(true);

      try {
        const response = await apiRequest({
          endpoint: `leadResources/${leadResourceId}`,
          method: HttpMethods.Patch,
          body: getPayload(form),
        });
        const body = await response.json();

        if (!response.ok) {
          throw new Error(getRestErrorMessage(body) || 'Lead resource update failed');
        }

        setLead(body);
        setForm(toForm(body));
        onLeadUpdated?.(body);
        showAlert(AlertType.Success, '', 'Lead updated');
      } catch (error) {
        showAlert(AlertType.Error, '', error?.message || 'Lead resource update failed');
      } finally {
        setIsSaving(false);
      }
    };

    React.useImperativeHandle(ref, () => ({ save: handleSave }), [handleSave]);

    React.useEffect(() => {
      onActionStateChange?.({ canSave, isSaving, canCreatePatient });
    }, [canSave, isSaving, canCreatePatient, onActionStateChange]);

    if (!leadResourceId) return null;

    const actions = hideActions ? null : (
      <div className={styles.footerActions}>
        {onCreatePatient ? (
          <Button
            id="lead-resource-create-patient"
            text={isCreatingPatient ? 'Creating...' : 'Create patient'}
            isLoading={isCreatingPatient}
            disabled={!canCreatePatient || isCreatingPatient}
            onClick={() => onCreatePatient()}
            title={
              canCreatePatient
                ? 'Create the patient record from this lead'
                : `Fix these fields first: ${[...missingFieldLabels, ...invalidFieldLabels].join(', ')}`
            }
          />
        ) : null}
        <Button
          id="lead-resource-call-save"
          text="Save"
          isLoading={isSaving}
          disabled={!canSave || isSaving || isCreatingPatient}
          onClick={handleSave}
        />
      </div>
    );

    return (
      <div className={[styles.root, isPage ? styles.page : ''].join(' ')}>
        <div className={styles.header}>
          <div>
            <div className={styles.eyebrow}>Lead resource</div>
            <h3 className={styles.title}>{title}</h3>
          </div>
        </div>

        {isCreatePatientStep && !isLoading && !errorText ? (
          <div className={canCreatePatient ? styles.requirementsReady : styles.requirementsPending}>
            {missingFieldLabels.length
              ? `Missing ${missingFieldLabels.length}: ${missingFieldLabels.join(', ')}. `
              : ''}
            {invalidFieldLabels.length
              ? `${invalidFieldLabels.join(' and ')} cannot contain numbers or symbols. `
              : ''}
            {missingFieldLabels.length || invalidFieldLabels.length
              ? 'Fix the highlighted fields to create the patient.'
              : 'All required fields are valid. Save your changes, then Create patient.'}
          </div>
        ) : null}

        {!isCreatePatientStep &&
        !isLoading &&
        !errorText &&
        (missingFieldLabels.length || invalidFieldLabels.length) ? (
          <div className={styles.requirementsPending}>
            {missingFieldLabels.length
              ? `Patient creation will need: ${missingFieldLabels.join(', ')}. `
              : ''}
            {invalidFieldLabels.length
              ? `${invalidFieldLabels.join(' and ')} cannot contain numbers or symbols. `
              : ''}
            You can still save the lead now and fill these in later.
          </div>
        ) : null}

        {isLoading ? <div className={styles.message}>Loading lead...</div> : null}
        {errorText ? <div className={styles.error}>{errorText}</div> : null}

        {!isLoading && !errorText ? (
          <>
            <div className={styles.lockedGrid}>
              <LockedValue label="Status" value={lead?.status} />
              <LockedValue label="Business Unit" value={lead?.businessUnit} />
              <LockedValue label="Lead Source" value={lead?.leadSource} />
              <LockedValue label="Zing ID" value={lead?.zingId} />
              <LockedValue label="Salesforce Lead ID" value={lead?.salesforceLeadId} />
              <LockedValue label="Salesforce Account ID" value={lead?.salesforceAccountId} />
            </div>

            <div className={styles.section}>
              <h4>Contact</h4>
              <div className={styles.grid}>
                <Input
                  id="lead-call-first-name"
                  label="First Name"
                  value={form.firstName}
                  {...inputFeedback('firstName')}
                  onTextChange={(value) => updateField('firstName', value)}
                />
                <Input
                  id="lead-call-last-name"
                  label="Last Name"
                  value={form.lastName}
                  {...inputFeedback('lastName')}
                  onTextChange={(value) => updateField('lastName', value)}
                />
                <Input
                  id="lead-call-phone"
                  label="Patient Phone Number"
                  value={form.phone}
                  {...inputFeedback('phone')}
                  onTextChange={(value) => updateField('phone', value)}
                />
                <Input
                  id="lead-call-email"
                  label="Patient Email"
                  value={form.email}
                  onTextChange={(value) => updateField('email', value)}
                />
                <NativeField
                  id="lead-call-dob"
                  label="Date of Birth"
                  type="date"
                  value={form.dateOfBirth}
                  {...fieldFeedback('dateOfBirth')}
                  onChange={(value) => updateField('dateOfBirth', value)}
                />
                <SelectField
                  id="lead-call-sex"
                  label="Sex"
                  placeholder="--None--"
                  position="absolute"
                  options={SEX_OPTIONS}
                  value={selectedSex}
                  {...fieldFeedback('sex')}
                  onChange={(option) => updateField('sex', option.value)}
                />
                <Input
                  id="lead-call-pcp"
                  label="PCP Name"
                  value={form.pcpName}
                  onTextChange={(value) => updateField('pcpName', value)}
                />
              </div>
            </div>

            <div className={styles.section}>
              <h4>Address</h4>
              <div className={styles.grid}>
                <Input
                  id="lead-call-street"
                  label="Street"
                  value={form.street}
                  {...inputFeedback('street')}
                  onTextChange={(value) => updateField('street', value)}
                />
                <Input
                  id="lead-call-street-2"
                  label="Street Address Line 2"
                  value={form.streetAddressLine2}
                  onTextChange={(value) => updateField('streetAddressLine2', value)}
                />
                <Input
                  id="lead-call-city"
                  label="City"
                  value={form.city}
                  {...inputFeedback('city')}
                  onTextChange={(value) => updateField('city', value)}
                />
                <SelectField
                  id="lead-call-state"
                  label="State"
                  placeholder="--None--"
                  position="absolute"
                  options={STATE_OPTIONS}
                  value={selectedState}
                  {...fieldFeedback('state')}
                  isSearchable
                  onChange={(option) => updateField('state', option?.value || '')}
                />
                <Input
                  id="lead-call-postal-code"
                  label="Postal Code"
                  value={form.postalCode}
                  {...inputFeedback('postalCode')}
                  onTextChange={(value) => updateField('postalCode', toZipCode(value))}
                />
              </div>
            </div>

            <div className={styles.section}>
              <h4>Call Outcome</h4>
              <div className={styles.grid}>
                <Select
                  id="lead-call-appointment-booked"
                  label="Appointment Booked?"
                  placeholder="--None--"
                  position="absolute"
                  options={YES_NO_OPTIONS}
                  value={selectedAppointmentBooked}
                  onChange={(option) => updateField('appointmentBooked', option?.value ?? null)}
                />
                <Input
                  id="lead-call-disqualification-reason"
                  label="Disqualification Reason"
                  value={form.disqualificationReason}
                  onTextChange={(value) => updateField('disqualificationReason', value)}
                />
                <NativeField
                  id="lead-call-disqualified-date"
                  label="Disqualified Date"
                  type="date"
                  value={form.disqualifiedDate}
                  onChange={(value) => updateField('disqualifiedDate', value)}
                />
                <NativeField
                  id="lead-call-disqualified-time"
                  label="Disqualified Time"
                  type="time"
                  value={form.disqualifiedTime}
                  onChange={(value) => updateField('disqualifiedTime', value)}
                />
              </div>
            </div>

            <div className={styles.section}>
              <h4>Caregiver</h4>
              <div className={styles.grid}>
                <Input
                  id="lead-call-caretaker-first-name"
                  label="Caretaker First Name"
                  value={form.caretakerFirstName}
                  onTextChange={(value) => updateField('caretakerFirstName', value)}
                />
                <Input
                  id="lead-call-caretaker-last-name"
                  label="Caretaker Last Name"
                  value={form.caretakerLastName}
                  onTextChange={(value) => updateField('caretakerLastName', value)}
                />
                <Input
                  id="lead-call-caretaker-phone"
                  label="Caretaker Phone Number"
                  value={form.caretakerPhoneNumber}
                  onTextChange={(value) => updateField('caretakerPhoneNumber', value)}
                />
                <Input
                  id="lead-call-caretaker-relationship"
                  label="Caretaker Relationship"
                  value={form.caretakerRelationship}
                  onTextChange={(value) => updateField('caretakerRelationship', value)}
                />
              </div>
            </div>

            <div className={styles.section}>
              <h4>Insurance</h4>
              <div className={styles.grid}>
                <Input
                  id="lead-call-mbi"
                  label="MBI Number"
                  value={form.mbiNumber}
                  onTextChange={(value) => updateField('mbiNumber', value)}
                />
                <Input
                  id="lead-call-contract-id"
                  label="Verified Contract ID"
                  value={form.verifiedContractId}
                  onTextChange={(value) => updateField('verifiedContractId', value)}
                />
                <Input
                  id="lead-call-plan-id"
                  label="Verified Plan ID"
                  value={form.verifiedPlanId}
                  onTextChange={(value) => updateField('verifiedPlanId', value)}
                />
                <Input
                  id="lead-call-payor-id"
                  label="Primary Insurance Payor ID"
                  value={form.primaryInsurancePayorId}
                  onTextChange={(value) => updateField('primaryInsurancePayorId', value)}
                />
                <NativeField
                  id="lead-call-insurance-start"
                  label="Primary Insurance Start"
                  type="date"
                  value={form.primaryInsuranceStart}
                  onChange={(value) => updateField('primaryInsuranceStart', value)}
                />
                <NativeField
                  id="lead-call-insurance-end"
                  label="Primary Insurance End"
                  type="date"
                  value={form.primaryInsuranceEnd}
                  onChange={(value) => updateField('primaryInsuranceEnd', value)}
                />
                <Input
                  id="lead-call-a1c-level"
                  label="Recent A1C Level"
                  value={form.recentA1cLevel}
                  onTextChange={(value) => updateField('recentA1cLevel', value)}
                />
                <NativeField
                  id="lead-call-a1c-date"
                  label="Recent A1C Date"
                  type="date"
                  value={form.recentA1cDate}
                  onChange={(value) => updateField('recentA1cDate', value)}
                />
              </div>
            </div>

            {showDiabetesIntake ? (
              <div className={styles.section}>
                <h4>Diabetes Intake</h4>
                <div className={styles.grid}>
                  <SelectField
                    id="lead-call-diabetes-type"
                    label="Type of Diagnosis"
                    placeholder="--None--"
                    position="absolute"
                    options={DIABETES_TYPE_OPTIONS}
                    value={selectedDiabetesType}
                    onChange={(option) => updateField('diabetesType', option?.value ?? null)}
                  />
                  <NativeField
                    id="lead-call-diagnosis-date"
                    label="Approximate Diagnosis Date"
                    type="date"
                    value={form.diagnosisDate}
                    onChange={(value) => updateField('diagnosisDate', value)}
                  />
                  <Input
                    id="lead-call-reading-frequency"
                    label="How Often They Check Readings"
                    value={form.readingFrequency}
                    onTextChange={(value) => updateField('readingFrequency', value)}
                  />
                  <SelectField
                    id="lead-call-takes-insulin"
                    label="Takes Insulin?"
                    placeholder="--None--"
                    position="absolute"
                    options={YES_NO_TEXT_OPTIONS}
                    value={selectedYesNo(form.takesInsulin)}
                    onChange={(option) => updateField('takesInsulin', option?.value ?? null)}
                  />
                  <SelectField
                    id="lead-call-has-pcp"
                    label="Has a Primary Care Physician?"
                    placeholder="--None--"
                    position="absolute"
                    options={YES_NO_TEXT_OPTIONS}
                    value={selectedYesNo(form.hasPcp)}
                    onChange={(option) => updateField('hasPcp', option?.value ?? null)}
                  />
                  <SelectField
                    id="lead-call-other-rpm"
                    label="On Another Remote Monitoring Program?"
                    placeholder="--None--"
                    position="absolute"
                    options={YES_NO_TEXT_OPTIONS}
                    value={selectedYesNo(form.onAnotherRemoteMonitoringProgram)}
                    onChange={(option) =>
                      updateField('onAnotherRemoteMonitoringProgram', option?.value ?? null)
                    }
                  />
                  <SelectField
                    id="lead-call-hospice"
                    label="On Hospice or Palliative Care?"
                    placeholder="--None--"
                    position="absolute"
                    options={YES_NO_TEXT_OPTIONS}
                    value={selectedYesNo(form.onHospiceOrPalliativeCare)}
                    onChange={(option) =>
                      updateField('onHospiceOrPalliativeCare', option?.value ?? null)
                    }
                  />
                  <Input
                    id="lead-call-patient-goal"
                    label="Main Goal With Perry Health"
                    value={form.patientGoal}
                    onTextChange={(value) => updateField('patientGoal', value)}
                  />
                  <SelectField
                    id="lead-call-has-smartphone"
                    label="Has a Smartphone?"
                    placeholder="--None--"
                    position="absolute"
                    options={YES_NO_TEXT_OPTIONS}
                    value={selectedYesNo(form.hasSmartphone)}
                    onChange={(option) => updateField('hasSmartphone', option?.value ?? null)}
                  />
                  <SelectField
                    id="lead-call-has-cgm"
                    label="Has a CGM?"
                    placeholder="--None--"
                    position="absolute"
                    options={YES_NO_TEXT_OPTIONS}
                    value={selectedYesNo(form.hasCgm)}
                    onChange={(option) => updateField('hasCgm', option?.value ?? null)}
                  />
                  <Input
                    id="lead-call-cgm-model"
                    label="CGM Model"
                    value={form.cgmModel}
                    onTextChange={(value) => updateField('cgmModel', value)}
                  />
                  <SelectField
                    id="lead-call-has-cgm-app"
                    label="Has CGM App Credentials?"
                    placeholder="--None--"
                    position="absolute"
                    options={YES_NO_TEXT_OPTIONS}
                    value={selectedYesNo(form.hasCgmApp)}
                    onChange={(option) => updateField('hasCgmApp', option?.value ?? null)}
                  />
                  <SelectField
                    id="lead-call-daily-readings"
                    label="Willing to Check Sugar Daily?"
                    placeholder="--None--"
                    position="absolute"
                    options={YES_NO_TEXT_OPTIONS}
                    value={selectedYesNo(form.willingToTakeDailyReadings)}
                    onChange={(option) =>
                      updateField('willingToTakeDailyReadings', option?.value ?? null)
                    }
                  />
                  <SelectField
                    id="lead-call-monthly-visits"
                    label="Willing to Have Monthly Visits?"
                    placeholder="--None--"
                    position="absolute"
                    options={YES_NO_TEXT_OPTIONS}
                    value={selectedYesNo(form.willingToHaveMonthlyVisits)}
                    onChange={(option) =>
                      updateField('willingToHaveMonthlyVisits', option?.value ?? null)
                    }
                  />
                </div>
              </div>
            ) : null}

            {showCognitiveIntake ? (
              <div className={styles.section}>
                <h4>Cognitive Intake</h4>
                <div className={styles.grid}>
                  <Input
                    id="lead-call-athena-department"
                    label="Athena Department"
                    value={form.athenaDepartment}
                    onTextChange={(value) => updateField('athenaDepartment', value)}
                  />
                  <SelectField
                    id="lead-call-online-screening"
                    label="Willing to Complete Online Cognitive Screening?"
                    placeholder="--None--"
                    position="absolute"
                    options={YES_NO_TEXT_OPTIONS}
                    value={selectedYesNo(form.willAttendOnlineCognitiveScreening)}
                    onChange={(option) =>
                      updateField('willAttendOnlineCognitiveScreening', option?.value ?? null)
                    }
                  />
                  <SelectField
                    id="lead-call-neurologist-visit"
                    label="Willing to Attend Neurologist Visit?"
                    placeholder="--None--"
                    position="absolute"
                    options={YES_NO_TEXT_OPTIONS}
                    value={selectedYesNo(form.willAttendNeurologistVisit)}
                    onChange={(option) =>
                      updateField('willAttendNeurologistVisit', option?.value ?? null)
                    }
                  />
                </div>
              </div>
            ) : null}

            <div className={styles.section}>
              <h4>Communication</h4>
              <div className={styles.checkboxGrid}>
                <CheckboxField
                  id="lead-call-voice-opt-in"
                  label="Voice Opt In"
                  checked={form.voiceOptIn}
                  disabled
                />
                <CheckboxField
                  id="lead-call-sms-opt-in"
                  label="SMS Opt In"
                  checked={form.smsOptIn}
                  disabled
                />
                <CheckboxField
                  id="lead-call-email-opt-in"
                  label="Email Opt In"
                  checked={form.emailOptIn}
                  disabled
                />
              </div>
            </div>

            {actions}
          </>
        ) : null}
      </div>
    );
  },
);

LockedValue.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
};

NativeField.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  type: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  errorText: PropTypes.string,
  warningText: PropTypes.string,
};

SelectField.propTypes = {
  errorText: PropTypes.string,
  warningText: PropTypes.string,
};

CheckboxField.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  checked: PropTypes.bool,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
};

LeadResourceCallForm.propTypes = {
  isPage: PropTypes.bool,
  leadResourceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onLeadUpdated: PropTypes.func,
  onCreatePatient: PropTypes.func,
  isCreatingPatient: PropTypes.bool,
  title: PropTypes.string,
  hideActions: PropTypes.bool,
  onActionStateChange: PropTypes.func,
};

export default LeadResourceCallForm;
